import React, { useState } from 'react';
import { AttractionCard } from './AttractionCard';
import { specifyCatLabels } from '../constants/translations';

export const THEME_CONFIG = {
  culture:  { icon:'🏛️', color:'#1e3a5f', accent:'#93c5fd', labels:{ zh:'文化體驗', zhCN:'文化体验', en:'Culture',        ja:'文化体験',     ko:'문화 체험'     } },
  nature:   { icon:'🌿', color:'#14532d', accent:'#4ade80', labels:{ zh:'大自然',   zhCN:'大自然',   en:'Nature',          ja:'大自然',       ko:'대자연'        } },
  play:     { icon:'🎡', color:'#4c1d95', accent:'#c4b5fd', labels:{ zh:'玩樂',     zhCN:'玩乐',     en:'Entertainment',   ja:'エンタメ',     ko:'엔터테인먼트'  } },
  shopping: { icon:'🛍️', color:'#9f1239', accent:'#fda4af', labels:{ zh:'購物',     zhCN:'购物',     en:'Shopping',        ja:'ショッピング', ko:'쇼핑'          } },
  food:     { icon:'🍜', color:'#c2410c', accent:'#fed7aa', labels:{ zh:'美食',     zhCN:'美食',     en:'Food & Dining',   ja:'グルメ',       ko:'미식'          } },
};

const CATEGORY_ORDER = ['culture','nature','play','shopping','food'];
const ALL_LABEL = { zh:'全部', zhCN:'全部', en:'All', ja:'すべて', ko:'전체' };
const PAGE_SIZE = 4;

export function CategorySection({ catKey, attractions, lang, t, cityKey, favorites, onToggleFav, onSelect, districtKey }) {
  const [subFilter, setSubFilter] = useState('all');
  const [page, setPage] = useState(0);

  // Reset filters when district changes
  React.useEffect(() => {
    setSubFilter('all');
    setPage(0);
  }, [districtKey]);

  const cfg     = THEME_CONFIG[catKey];
  const langKey = lang==='zh-CN'?'zhCN':lang==='en'?'en':lang==='ja'?'ja':lang==='ko'?'ko':'zh';

  const catAttractions = attractions.filter(a => a.category === catKey);
  if (catAttractions.length === 0) return null;

  // Dynamic specify_cat chips from actual data — no keywords, exact match only
  const specifyOptions = [...new Set(
    catAttractions.filter(a => a.specify_cat).map(a => a.specify_cat)
  )].sort();

  const displayed = subFilter === 'all'
    ? catAttractions
    : catAttractions.filter(a => a.specify_cat === subFilter);

  const totalPages = Math.ceil(displayed.length / PAGE_SIZE);
  const pageItems  = displayed.slice(page * PAGE_SIZE, (page+1) * PAGE_SIZE);

  const catLabel = cfg.labels[langKey] || cfg.labels.zh;
  const allLabel = ALL_LABEL[langKey]  || ALL_LABEL.zh;

  return (
    <div style={{ marginBottom:32 }}>
      {/* Section header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
        <div style={{ width:38, height:38, borderRadius:12, background:cfg.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:19, flexShrink:0, boxShadow:`0 4px 12px ${cfg.color}50` }}>
          {cfg.icon}
        </div>
        <div>
          <p style={{ margin:0, fontSize:16, fontWeight:900, color:'#0f172a', lineHeight:1.2 }}>{catLabel}</p>
          <p style={{ margin:0, fontSize:10, color:'#94a3b8', fontWeight:600 }}>{catAttractions.length} {lang==='en'?'spots':lang==='ja'?'件':lang==='ko'?'곳':'個景點'}</p>
        </div>
      </div>

      {/* specify_cat chips — dynamic, only shown if there are sub-options */}
      {specifyOptions.length > 0 && (
        <div style={{ display:'flex', gap:7, flexWrap:'wrap', marginBottom:12 }}>
          <button onClick={() => { setSubFilter('all'); setPage(0); }}
            style={{ padding:'5px 14px', borderRadius:20, border:'1.5px solid', fontSize:11, fontWeight:900, cursor:'pointer', transition:'all 0.15s',
              borderColor: subFilter==='all' ? cfg.color : '#e2e8f0',
              background:  subFilter==='all' ? cfg.color : 'white',
              color:       subFilter==='all' ? 'white'   : '#64748b' }}>
            {allLabel}
          </button>
          {specifyOptions.map(sc => {
            const active = subFilter === sc;
            const cnt = catAttractions.filter(a => a.specify_cat === sc).length;
            return (
              <button key={sc} onClick={() => { setSubFilter(sc); setPage(0); }}
                style={{ padding:'5px 14px', borderRadius:20, border:'1.5px solid', fontSize:11, fontWeight:900, cursor:'pointer', transition:'all 0.15s',
                  borderColor: active ? cfg.color : '#e2e8f0',
                  background:  active ? `${cfg.color}18` : 'white',
                  color:       active ? cfg.color : '#64748b' }}>
                {(specifyCatLabels[sc] && specifyCatLabels[sc][lang]) || sc}
                <span style={{ marginLeft:4, fontSize:9, opacity:0.65 }}>({cnt})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Cards grid */}
      {displayed.length === 0 ? (
        <p style={{ fontSize:12, color:'#94a3b8', margin:'8px 0 0', textAlign:'center', padding:'20px 0' }}>
          {lang==='en'?'No matches found':lang==='ja'?'該当なし':lang==='ko'?'결과 없음':'暫無符合景點'}
        </p>
      ) : null}
      <div style={{ position:'relative' }}>
        {totalPages > 1 && page > 0 && (
          <button aria-label="上一頁" onClick={() => setPage(p => Math.max(0, p-1))}
            style={{ position:'absolute', left:-20, top:'50%', transform:'translateY(-50%)', zIndex:10,
              width:44, height:44, borderRadius:'50%', border:'1.5px solid #e2e8f0',
              background:'white', color:'#374151', cursor:'pointer', display:'flex', alignItems:'center',
              justifyContent:'center', fontSize:24, fontWeight:900, boxShadow:'0 4px 16px rgba(0,0,0,0.1)' }}>‹</button>
        )}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(148px,1fr))', gap:12 }}>
          {pageItems.map((a, i) => (
            <AttractionCard key={`${catKey}-${subFilter}-${page}-${i}`} a={a} idx={page*PAGE_SIZE+i}
              cityKey={cityKey} t={t} lang={lang} favorites={favorites} onToggleFav={onToggleFav} onSelect={onSelect}/>
          ))}
        </div>
        {totalPages > 1 && page < totalPages-1 && (
          <button aria-label="下一頁" onClick={() => setPage(p => Math.min(totalPages-1, p+1))}
            style={{ position:'absolute', right:-20, top:'50%', transform:'translateY(-50%)', zIndex:10,
              width:44, height:44, borderRadius:'50%', border:'1.5px solid #e2e8f0',
              background:'white', color:'#374151', cursor:'pointer', display:'flex', alignItems:'center',
              justifyContent:'center', fontSize:24, fontWeight:900, boxShadow:'0 4px 16px rgba(0,0,0,0.1)' }}>›</button>
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', gap:6, marginTop:12 }}>
          {Array.from({ length:totalPages }, (_, i) => (
            <button key={i} aria-label={`第 ${i+1} 頁`} onClick={() => setPage(i)}
              style={{ width:i===page?20:7, height:7, borderRadius:4, border:'none',
                background:i===page ? cfg.color : '#e2e8f0', cursor:'pointer', padding:0, transition:'all 0.25s' }}/>
          ))}
        </div>
      )}
    </div>
  );
}

export function ThemedSections({ attractions, lang, t, cityKey, favorites, onToggleFav, onSelect, districtKey }) {
  if (!attractions?.length) return null;
  return (
    <div>
      {CATEGORY_ORDER.map(cat => (
        <CategorySection key={cat} catKey={cat} attractions={attractions} lang={lang} t={t}
          cityKey={cityKey} favorites={favorites} onToggleFav={onToggleFav} onSelect={onSelect}
          districtKey={districtKey}/>
      ))}
    </div>
  );
}
